import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Link,
  IconButton,
  InputAdornment
} from '@mui/material';
import { ChevronLeft, ChevronRight, Visibility, VisibilityOff, KeyboardCapslock } from '@mui/icons-material';
import InstallAppButton from '../components/InstallAppButton';
import { quikqAuthService } from '../services/quikqAuth';
import { useNavigate } from 'react-router-dom';
import { logger, LogCategory } from '../services/logger';

// Fuel station images data - optimized with correct file names
const fuelStations = [
  { name: 'Thorntons', image: '/FuelStations/Thorntons.png' },
  { name: 'TAC', image: '/FuelStations/TAC.png' },
  { name: 'SAPP', image: '/FuelStations/SAPP.png' },
  { name: 'TA', image: '/FuelStations/TA.png' },
  { name: 'Caseys', image: '/FuelStations/CASEYS.png' },
  { name: 'BP Helios', image: '/FuelStations/BPHelios.png' },
  { name: 'Petro', image: '/FuelStations/Petro.png' },
  { name: '7-Eleven', image: '/FuelStations/7Eleven.png' },
  { name: 'Raceway', image: '/FuelStations/raceway.png' },
  { name: 'RaceTrac', image: '/FuelStations/RaceTrac.png' }
];

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({
    userId: '',
    password: '',
    startCode: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  const [backgroundImage, setBackgroundImage] = useState<string>('');

  // CapsLock keydown often reports the previous lock state (and some browsers skip keyup).
  // Toggle immediately on that key; other keys/clicks sync from getModifierState.
  useEffect(() => {
    if (!passwordFocused) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'CapsLock') {
        if (!event.repeat) {
          setCapsLockOn((prev) => !prev);
        }
        return;
      }
      setCapsLockOn(event.getModifierState('CapsLock'));
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'CapsLock') return;
      setCapsLockOn(event.getModifierState('CapsLock'));
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [passwordFocused]);

  // Ensure login page can use full-screen background
  useEffect(() => {
    const rootElement = document.getElementById('root');
    rootElement?.classList.add('login-full-screen');
    return () => {
      rootElement?.classList.remove('login-full-screen');
    };
  }, []);

  // Determine background image based on user local time and screen size
  useEffect(() => {
    const updateBackgroundImage = () => {
      const hour = new Date().getHours();
      const isDayTime = hour >= 6 && hour < 18;
      const isMobile = window.innerWidth < 768;

      let rawImagePath = '';

      if (isMobile) {
        rawImagePath = isDayTime
          ? '/Logindaynight/MobileDay.png'
          : '/Logindaynight/MobileNight.png';
      } else {
        rawImagePath = isDayTime
          ? '/Logindaynight/DesktopDay.png'
          : '/Logindaynight/DesktopNight.png';
      }

      // Use original path string, but wrap it in quotes in CSS url
      const finalPath = rawImagePath;

      setBackgroundImage(finalPath);

      // Apply background directly to body so it always covers full viewport
      const body = document.body;
      if (finalPath) {
        body.style.backgroundImage = `url("${finalPath}")`;
        body.style.backgroundSize = 'cover';
        body.style.backgroundRepeat = 'no-repeat';
        body.style.backgroundPosition = 'center';
        // On mobile browsers "fixed" attachment often causes issues, so use "scroll" instead
        body.style.backgroundAttachment = isMobile ? 'scroll' : 'fixed';
      }
    };

    updateBackgroundImage();
    window.addEventListener('resize', updateBackgroundImage);

    // Refresh background periodically in case user keeps page open around day/night change
    const intervalId = window.setInterval(updateBackgroundImage, 5 * 60 * 1000);

    return () => {
      window.removeEventListener('resize', updateBackgroundImage);
      window.clearInterval(intervalId);
      // Reset body background when leaving login page
      const body = document.body;
      body.style.backgroundImage = '';
      body.style.backgroundSize = '';
      body.style.backgroundRepeat = '';
      body.style.backgroundPosition = '';
      body.style.backgroundAttachment = '';
    };
  }, []);


  // Carousel variables - responsive images per slide
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [imagesPerSlide, setImagesPerSlide] = useState(2); // Default to mobile (2)
  
  // Update images per slide based on screen size
  useEffect(() => {
    const updateImagesPerSlide = () => {
      const newImagesPerSlide = window.innerWidth < 600 ? 2 : 4;
      if (newImagesPerSlide !== imagesPerSlide) {
        setImagesPerSlide(newImagesPerSlide);
        // Reset to first slide when images per slide changes
        setCurrentSlideIndex(0);
      }
    };
    
    updateImagesPerSlide();
    window.addEventListener('resize', updateImagesPerSlide);
    return () => window.removeEventListener('resize', updateImagesPerSlide);
  }, [imagesPerSlide]);
  
  const totalSlides = Math.ceil(fuelStations.length / imagesPerSlide);

  // Auto-rotate carousel every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isTransitioning) {
        setIsTransitioning(true);
        setTimeout(() => {
          setCurrentSlideIndex((prev) => (prev + 1) % totalSlides);
          setTimeout(() => setIsTransitioning(false), 300);
        }, 150);
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [totalSlides, isTransitioning]);

  const changeSlide = (newIndex: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlideIndex(newIndex);
      setTimeout(() => setIsTransitioning(false), 300);
    }, 150);
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setCredentials(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    // Normalize userId/email to avoid case-sensitivity issues
    const normalizedUserIdRaw = credentials.userId.trim();
    const normalizedUserId = normalizedUserIdRaw.includes('@')
      ? normalizedUserIdRaw.toLowerCase()
      : normalizedUserIdRaw;

    // Keep UI state in sync (so users see what is actually used)
    if (credentials.userId !== normalizedUserId) {
      setCredentials((prev) => ({ ...prev, userId: normalizedUserId }));
    }

    // Validate required fields
    if (!normalizedUserId) {
      setError('User ID is required');
      setLoading(false);
      return;
    }

    if (!credentials.password.trim()) {
      setError('Password is required');
      setLoading(false);
      return;
    }

    const normalizedStartCode = credentials.startCode.trim();
    if (!normalizedStartCode) {
      setError('Start code is required');
      setLoading(false);
      return;
    }

    // Log the credentials being sent (without password)
    // console.log('🔍 Submitting login with:', {
    //   userId: credentials.userId,
    //   hasPassword: !!credentials.password,
    //   startCode: credentials.startCode
    // });

    try {
      const user = await quikqAuthService.login({
        ...credentials,
        userId: normalizedUserId,
        startCode: normalizedStartCode,
      });
      logger.info(LogCategory.NAVIGATION, `User logged in, redirecting to ${user.role === 'DRIVER' ? 'map' : 'dashboard'}`, { role: user.role });
      
      // Redirect drivers to map, regular users to dashboard
      if (user.role === 'DRIVER') {
        navigate('/map');
      } else {
        navigate('/');
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      const errorMessageLower = error.message.toLowerCase();
      
      // Check if this is a geolocation restriction error
      const isGeolocationError = errorMessageLower.includes('united states') || 
                                  errorMessageLower.includes('not available') ||
                                  errorMessageLower.includes('geolocation') ||
                                  errorMessageLower.includes('region');
      
      // Log with appropriate category
      if (isGeolocationError) {
        logger.error(LogCategory.SECURITY, 'Login blocked: Geolocation restriction in Login component', error, { 
          userId: normalizedUserId,
          reason: 'geolocation_restriction'
        });
      } else {
        logger.error(LogCategory.AUTH, 'Login error in Login component', error, { userId: normalizedUserId });
      }
      
      // Provide more specific error messages based on the error
      let errorMessage = 'Login failed';
      if (err instanceof Error) {
        if (err.message.includes('United States') || err.message.includes('not available') || err.message.includes('region')) {
          errorMessage = 'This service is only available in the United States. Access from your current location is not permitted.';
        } else if (err.message.includes('Invalid credentials') || err.message.includes('Authentication failed')) {
          errorMessage = 'Invalid credentials. Please check your User ID, Password, and Start Code.';
        } else if (err.message.includes('Invalid Start Code format')) {
          errorMessage = 'Invalid Start Code format. Please check your Start Code and try again.';
        } else if (err.message.includes('Request timed out')) {
          errorMessage = 'Request timed out. Please check your internet connection and try again.';
        } else if (err.message.includes('Unable to connect')) {
          errorMessage = 'Unable to connect to the server. Please check your internet connection or try again later.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      className="login-page-background"
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: { xs: 'center', md: 'flex-end' },
        boxSizing: 'border-box'
      }}
      style={backgroundImage ? { backgroundImage: `url("${backgroundImage}")` } : undefined}
    >
      {/* Desna polovina ekrana – blurovani overlay */}
      <Box
        sx={{
          width: { xs: '100%', md: '50%' },
          height: '100vh',
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'center',
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 3, md: 4 },
          boxSizing: 'border-box',
          backgroundColor: 'rgba(255, 255, 255, 0.74)',
          backdropFilter: 'blur(13px)',
          WebkitBackdropFilter: 'blur(13px)',
          overflowY: { xs: 'auto', md: 'hidden' }
        }}
      >
        <Container
          maxWidth="sm"
          sx={{
            px: 0,
            width: '100%',
            maxWidth: '480px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: '100%'
          }}
        >
          <Box>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3, md: 3 },
                width: '100%',
                borderRadius: 3,
                backgroundColor: 'transparent',
                border: 'none',
                boxShadow: 'none',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%',
                boxSizing: 'border-box',
                overflow: 'hidden'
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                mb: { xs: 1.5, sm: 2, md: 2.5 } 
              }}>
                <Box
                  component="img"
                  src="/NSFLogo.png" 
                  alt="NSF Logo" 
                  sx={{ 
                    height: { xs: '60px', sm: '70px', md: '80px' }, 
                    width: 'auto',
                    maxWidth: { xs: '150px', sm: '180px', md: '200px' }
                  }} 
                />
              </Box>
              <Typography
                variant="h6"
                align="center"
                gutterBottom
                sx={{
                  fontSize: { xs: 15, sm: 17, md: 18 },
                  color: 'rgba(0, 0, 0, 0.75)',
                }}
              >
                Sign in to your account
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: { xs: 1.5, sm: 2, md: 2.5 } }}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} sx={{ mt: { xs: 0.5, sm: 1, md: 1.5 } }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="userId"
                  label="User ID"
                  name="userId"
                  autoComplete="email"
                  value={credentials.userId}
                  onChange={handleInputChange('userId')}
                  placeholder="Enter your user ID or email"
                  helperText="Enter your email address or user ID"
                  sx={{ 
                    mb: { xs: 1.5, sm: 2, md: 2 },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.55)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.75)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                  }}
                  FormHelperTextProps={{
                    sx: { color: 'rgba(0, 0, 0, 0.75)' }
                  }}
                  size="medium"
                />
                
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={credentials.password}
                  onChange={handleInputChange('password')}
                  onFocus={() => setPasswordFocused(true)}
                  onClick={(event) => setCapsLockOn(event.getModifierState('CapsLock'))}
                  onBlur={() => {
                    setPasswordFocused(false);
                    setCapsLockOn(false);
                  }}
                  placeholder="Enter your password"
                  helperText={
                    capsLockOn ? (
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <KeyboardCapslock sx={{ fontSize: 16 }} />
                        Caps Lock is on
                      </Box>
                    ) : (
                      'Enter your password'
                    )
                  }
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            type="button"
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            onClick={() => setShowPassword((prev) => !prev)}
                            onMouseDown={(event) => event.preventDefault()}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                    formHelperText: {
                      sx: { color: capsLockOn ? 'warning.dark' : 'rgba(0, 0, 0, 0.75)' },
                    },
                  }}
                  sx={{ 
                    mb: { xs: 1.5, sm: 2, md: 2 },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.55)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.75)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                    '& input::-ms-reveal, & input::-ms-clear': {
                      display: 'none',
                    },
                  }}
                  size="medium"
                />
                
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="startCode"
                  label="Start Code"
                  id="startCode"
                  value={credentials.startCode}
                  onChange={handleInputChange('startCode')}
                  placeholder="Enter your start code (6-8 digits)"
                  helperText="Enter your 6-8 digit start code"
                  sx={{ 
                    mb: { xs: 1.5, sm: 2, md: 2 },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.55)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.75)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                  }}
                  FormHelperTextProps={{
                    sx: { color: 'rgba(0, 0, 0, 0.75)' }
                  }}
                  size="medium"
                />
                
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ 
                    mt: { xs: 1.25, sm: 1.75, md: 2 }, 
                    mb: { xs: 1.25, sm: 1.75, md: 1.75 },
                    py: { xs: 1.1, sm: 1.3, md: 1.4 },
                    fontSize: { xs: 13, sm: 15, md: 16 },
                    minHeight: { xs: 40, sm: 44, md: 46 }
                  }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Sign In'}
                </Button>

                {/* Install app button (PWA) */}
                <Box sx={{ mt: { xs: 0.75, sm: 1, md: 1 } }}>
                  <InstallAppButton />
                </Box>
                
                <Box sx={{ textAlign: 'center', mt: { xs: 1.5, sm: 2, md: 2 } }}>
                  <Link 
                    href="#" 
                    variant="body2"
                    onClick={() => navigate('/forgot-password')}
                    sx={{ fontSize: { xs: 13, sm: 14, md: 16 } }}
                  >
                    Forgot User ID? Reset Password
                  </Link>
                </Box>
                
                <Box sx={{ textAlign: 'center', mt: { xs: 0.5, sm: 1, md: 1.5 } }}>
                  <Link 
                    href="#" 
                    variant="body2"
                    sx={{ fontSize: { xs: 13, sm: 14, md: 16 } }}
                  >
                    Contact Support
                  </Link>
                </Box>
              </Box>
              {/* Fuel Stations Carousel – unutar istog blur kontejnera */}
              <Box sx={{ 
                mt: { xs: 1.5, sm: 2, md: 2.5 },
                width: '100%',
                overflowX: 'hidden',
                mx: 'auto',
                pb: { xs: 0.5, md: 0 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <Typography variant="h6" align="center" color="textSecondary" gutterBottom
                  sx={{ 
                    fontSize: { xs: 14, sm: 16, md: 17 }, 
                    mb: 1,
                    fontWeight: 700,
                    color: 'rgba(0, 0, 0, 0.75)'
                  }}>
                  Our Partners
                </Typography>
                
                {/* Simple CSS Carousel */}
                <Box sx={{
                  position: 'relative',
                  width: '100%',
                  height: { xs: '80px', sm: '90px', md: '100px' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  mx: 'auto'
                }}>
                  {/* Previous Button - Hidden on mobile */}
                  <IconButton
                    onClick={() => changeSlide((currentSlideIndex - 1 + totalSlides) % totalSlides)}
                    sx={{
                      position: 'absolute',
                      left: { xs: 0, sm: -24, md: -32 },
                      zIndex: 10,
                      display: { xs: 'none', sm: 'flex' },
                      color: '#ffffff',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        color: '#e0e0e0'
                      }
                    }}
                  >
                    <ChevronLeft sx={{ fontSize: { sm: 22, md: 24 } }} />
                  </IconButton>

                  {/* Image Container */}
                  <Box sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: { xs: '16px', sm: '12px', md: '15px' },
                    opacity: isTransitioning ? 0.3 : 1,
                    transition: 'opacity 0.3s ease, transform 0.3s ease',
                    transform: isTransitioning ? 'scale(0.95)' : 'scale(1)',
                    overflow: 'hidden',
                    margin: '0 auto',
                    maxWidth: '100%'
                  }}>
                    {/* Render images per slide - 3 on mobile, 4 on larger screens */}
                    {Array.from({ length: imagesPerSlide }).map((_, imageIndex) => {
                      const stationIndex = currentSlideIndex * imagesPerSlide + imageIndex;
                      const station = fuelStations[stationIndex];
                      
                      if (!station) return null;
                      
                      return (
                        <Box
                          key={stationIndex}
                          component="img"
                          src={station.image}
                          alt={station.name}
                          sx={{
                            height: { xs: '72px', sm: '60px', md: '70px' },
                            width: 'auto',
                            maxWidth: { xs: '130px', sm: '110px', md: '120px' },
                            objectFit: 'contain',
                            filter: 'brightness(0.95) contrast(1.1)',
                            transition: 'transform 0.3s ease',
                            flexShrink: 0,
                            '&:hover': {
                              transform: { xs: 'none', sm: 'scale(1.05)' }
                            }
                          }}
                          onError={(e) => {
                            console.error(`Failed to load image: ${station.image}`);
                            e.currentTarget.style.opacity = '0.5';
                          }}
                        />
                      );
                    })}
                  </Box>

                  {/* Next Button - Hidden on mobile */}
                  <IconButton
                    onClick={() => changeSlide((currentSlideIndex + 1) % totalSlides)}
                    sx={{
                      position: 'absolute',
                      right: { xs: 0, sm: -24, md: -32 },
                      zIndex: 10,
                      display: { xs: 'none', sm: 'flex' },
                      color: '#ffffff',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        color: '#e0e0e0'
                      }
                    }}
                  >
                    <ChevronRight sx={{ fontSize: { sm: 22, md: 24 } }} />
                  </IconButton>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Login; 